import { useCallback, useState } from "react";
import { useGomakeAxios } from "@/hooks";
import { getImpositionSimulationData } from "@/services/impositionService";
import type { ImpositionSimulationDto } from "@/types/imposition";

export interface UseImpositionSimulationOptions {
  /** Recoil `currentCalculationConnectionId` — same value as SignalR connection id for pricing */
  signalRConnectionId: string | null | undefined;
  /** From the selected workflow when present */
  productType?: string | null;
}

/**
 * Fetches imposition data and exposes modal-friendly state (loading / error / payload).
 * Presentation components should render loading and errors from this hook.
 */
export function useImpositionSimulation({
  signalRConnectionId,
  productType,
}: UseImpositionSimulationOptions) {
  const { callApi } = useGomakeAxios();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<ImpositionSimulationDto | null>(null);

  const close = useCallback(() => {
    setOpen(false);
    setError(null);
  }, []);

  /**
   * Validates connection id, loads simulation (no global loading overlay — local `loading` only).
   * Opens the modal on success; keeps it closed and sets `error` on failure.
   */
  const fetchAndOpen = useCallback(async () => {
    const id = signalRConnectionId?.trim();
    if (!id) {
      setError("missingConnectionId");
      setData(null);
      setOpen(true);
      return;
    }
    setLoading(true);
    setError(null);
    setData(null);
    try {
      const res = await getImpositionSimulationData(
        callApi,
        {
          signalRConnectionId: id,
          productType:
            productType != null && String(productType).trim() !== ""
              ? productType
              : undefined,
        },
        false
      );
      if (!res.success) {
        setError(res.message || "requestFailed");
        setData(null);
        setOpen(true);
        return;
      }
      setData(res.data);
      setOpen(true);
    } catch {
      setError("networkError");
      setData(null);
      setOpen(true);
    } finally {
      setLoading(false);
    }
  }, [callApi, productType, signalRConnectionId]);

  return {
    open,
    loading,
    error,
    data,
    fetchAndOpen,
    close,
    setOpen,
  };
}
